/**
 * Grow lights — a thin horizontal light bar mounted under each greenhouse shelf,
 * lighting the whole row of pots on that shelf. One light per shelf (per rack-tier).
 * The top shelf has no shelf above it, so its light hangs from added support beams.
 *
 * A light is FULLY WIRED: it renders and casts a soft glow over its shelf's pots and DRAWS POWER
 * (per lit fixture by tier, from the grid — see sim/power.ts). What it DOES is the favoured-light model
 * below: at NIGHT, a plant under its FAVOURED colour loses the night slow-down and gives off experience.
 * Placed lights are persisted by their "rack-tier" key.
 */

/** Price of the player's FIRST grow light. Each one after it costs more (see `nextGrowLightPrice`). */
export const GROW_LIGHT_PRICE = 90;
/** Player LEVEL required to buy a grow light (needs the power chain to run). */
export const GROW_LIGHT_UNLOCK_LEVEL = 20;

/** Hard cap on grow lights: one per shelf, 5 racks × 3 tiers. */
const MAX_GROW_LIGHTS = 15;
/** Price of each grow light by how many the player ALREADY owns (index 0 = the first one).
 *
 *  Hand-tuned rather than a bare multiplier, in the same spirit as `nextShelfPrice`: the ramp is a steady
 *  ~1.22× per unit early, easing to ~1.18× at the top so the 15th light lands at 1,300 — roughly the cost
 *  of a tier-4 upgrade, i.e. a goal you save for rather than a wall. #1 is still 90, so nothing the player
 *  could buy yesterday got more expensive today. Full ladder + running total in BALANCE.md Appendix C. */
const GROW_LIGHT_PRICES = [90, 110, 135, 165, 200, 245, 300, 365, 440, 530, 640, 770, 920, 1100, 1300];
/** The cost of the NEXT grow light given how many are already placed, or null at the cap. */
export function nextGrowLightPrice(owned: number): number | null {
  if (owned >= MAX_GROW_LIGHTS) return null;
  return GROW_LIGHT_PRICES[Math.max(0, owned)] ?? GROW_LIGHT_PRICES[GROW_LIGHT_PRICES.length - 1]!;
}

/** Stable key for a shelf's grow light. */
export function lightKey(rack: number, tier: number): string {
  return `${rack}-${tier}`;
}

// ---- GROW-LIGHT TIERS + COLORS ----------------------------------------------
// Each PLACED grow light can be UPGRADED in place through a series of better
// fixtures (tier 0 = the base bar; tiers 1–4 are upgrades). Upgrade prices are tuned AGAINST the
// purchase ladder above: maxing one light costs 2,200, a little more than the 1,300 the 15th light
// costs, so "improve what I have" and "cover one more shelf" stay comparable choices all the way up.
// A higher tier draws more power, UNLOCKS more selectable light COLOURS (white → blue → red →
// magenta → blacklight) and raises the share of a plant's experience its favoured light pays out
// (`favoredOrbXpShare`, 10% → 50%).
// The fixture keeps its full-shelf WIDTH at every tier; only its vertical housing
// grows a little. Upgraded + recoloured from the light's detail modal.

/**
 * THE FAVOURED-LIGHT MODEL. Every species has ONE favoured colour (`FlowerSpecies.preferredLight`), and a
 * shelf's light shines ONE colour — so which plants share a shelf is the player's optimisation.
 *
 * While it is NIGHT and a plant's shelf light is ON in its FAVOURED colour, two things happen, and nothing
 * happens to any other plant:
 *
 * 1. NO NIGHT SLOW-DOWN. Every plant grows NIGHT_GROWTH_PENALTY (−20%) slower at night; a favoured-lit plant
 *    doesn't — it grows at full day speed through the night.
 * 2. MICRO-EXPERIENCE. The plant gives off small XP orbs that float to the Gardening bar. They are paid from
 *    GROWTH, not wall time: each bit of growth made under favoured night light earns that fraction of
 *    `favoredOrbXpShare(tier)` × the plant's harvest XP. So a plant that spends its WHOLE growth under
 *    favoured night light pays exactly the share (50% at the top tier); half its growth pays half of it.
 *    Growth after maturity pays nothing. The accrual + the staggered orbs live in useGreenhouse.
 *
 * WHAT THIS REPLACED (Sep 2026): a +34–55% preferred-colour SPEED boost, plus a per-colour HARVEST perk
 * (white XP, blue seed-back, red gold, magenta both, blacklight 10× XP lottery) chosen by the colour a plant
 * had spent the most nights under. The perks duplicated three compost effects, the history was invisible,
 * and the speed boost had no headroom under MAX_SPEED_MULT. See reports/grow-light-payoffs.html.
 */
export interface LightColor {
  id: string;
  label: string;
  /** the lit tube colour. */
  tube: number;
  /** the downward glow / beam colour. */
  glow: number;
}

/** All grow-light colours, by id. Tiers unlock progressively more of these (see GROW_LIGHT_TIERS). */
export const LIGHT_COLORS: Record<string, LightColor> = {
  white:   { id: "white",   label: "White",      tube: 0xfdfbf2, glow: 0xfff6df },
  blue:    { id: "blue",    label: "Blue",       tube: 0x7db4ff, glow: 0x5a9bff },
  red:     { id: "red",     label: "Red",        tube: 0xff7a6a, glow: 0xff5a4a },
  magenta: { id: "magenta", label: "Magenta",    tube: 0xff7ad8, glow: 0xff4ac6 },
  uv:      { id: "uv",      label: "Blacklight", tube: 0x9d6bff, glow: 0x6a32d6 },
};

/** Is a plant with favoured colour `favoredId` being lit by its favoured colour? (Night + power gating is the
 *  caller's: pass the colour actually shining on the pot right now, or undefined.) */
export function isFavoredLight(litColorId: string | undefined, favoredId: string | undefined): boolean {
  return !!litColorId && !!favoredId && litColorId === favoredId;
}

/** The share of a plant's harvest XP its favoured light pays out as micro-orbs over a WHOLE growth spent
 *  under it at night: 10% at the first fixture (tier 0), rising linearly to 50% at the top tier. */
export function favoredOrbXpShare(tier: number): number {
  return 0.10 + 0.40 * (clampTier(tier) / GROW_LIGHT_MAX_TIER);
}

/** How often a favoured-lit plant sends out a micro-orb: every ORB_INTERVAL_MS, jittered by ±ORB_JITTER per
 *  orb (so a shelf twinkles rather than pulsing in step). A pot's first orb is offset randomly too. */
export const ORB_INTERVAL_MS = 7_000;
export const ORB_JITTER = 0.45;
/** The next orb time after `now` for one pot — the interval with its jitter applied. */
export function nextOrbAt(now: number, rand: () => number = Math.random): number {
  return now + ORB_INTERVAL_MS * (1 - ORB_JITTER + 2 * ORB_JITTER * rand());
}

export interface GrowLightTier {
  /** Tier index (0 = base Grow Light). */
  tier: number;
  name: string;
  icon: string;
  /** Power draw in Watts while lit (at this tier). */
  watts: number;
  /** Glow strength multiplier (1 = base) — brighter/wider beam at higher tiers. */
  glowStrength: number;
  /** Extra vertical housing height (px) above the bar — the WIDTH never changes. */
  housing: number;
  /** Coins to upgrade FROM the previous tier TO this one. 0 for the base. */
  upgradePrice: number;
  /** Colour ids selectable at this tier (cumulative: each tier adds more). */
  colors: string[];
  /** One-line flavour shown in the light's detail modal. */
  blurb: string;
}

const GROW_LIGHT_TIERS: GrowLightTier[] = [
  { tier: 0, name: "Grow Light",      icon: "💡", watts: 20, glowStrength: 1.0,  housing: 0,  upgradePrice: 0,    colors: ["white"],
    blurb: "A simple white bar that lights the whole shelf." },
  { tier: 1, name: "Bright Bar",      icon: "💡", watts: 26, glowStrength: 1.25, housing: 5,  upgradePrice: 160,  colors: ["white", "blue"],
    blurb: "A brighter bar that adds a blue channel." },
  { tier: 2, name: "Spectrum Bar",    icon: "🔆", watts: 34, glowStrength: 1.5,  housing: 9,  upgradePrice: 340,  colors: ["white", "blue", "red"],
    blurb: "Adds a deep-red bloom channel." },
  { tier: 3, name: "Full Spectrum",   icon: "🌈", watts: 44, glowStrength: 1.8,  housing: 13, upgradePrice: 620,  colors: ["white", "blue", "red", "magenta"],
    blurb: "A full-spectrum LED panel: white, blue, red and magenta." },
  { tier: 4, name: "Quantum Array",   icon: "✨", watts: 56, glowStrength: 2.15, housing: 17, upgradePrice: 1080, colors: ["white", "blue", "red", "magenta", "uv"],
    blurb: "A quantum-dot array adding the blacklight (UV) channel." },
];

export const GROW_LIGHT_MAX_TIER = GROW_LIGHT_TIERS.length - 1;

function clampTier(tier: number): number {
  return Math.max(0, Math.min(GROW_LIGHT_MAX_TIER, Math.round(tier)));
}

/** The tier definition for a grow light at the given tier (clamped). */
export function growLightTier(tier: number): GrowLightTier {
  return GROW_LIGHT_TIERS[clampTier(tier)]!;
}
/** The next tier up (or null if already maxed). */
export function nextGrowLightTier(tier: number): GrowLightTier | null {
  return tier < GROW_LIGHT_MAX_TIER ? GROW_LIGHT_TIERS[tier + 1]! : null;
}
/** Power draw (W) for a grow light at the given tier. */
export function growLightWatts(tier: number): number {
  return GROW_LIGHT_TIERS[clampTier(tier)]!.watts;
}
/** The colour ids a light may select at the given tier. */
export function colorsForTier(tier: number): string[] {
  return GROW_LIGHT_TIERS[clampTier(tier)]!.colors;
}
/** Resolve a colour id to its definition. An unknown / missing id falls back to WHITE — the tier-0
 *  colour every light starts on. The fallback used to name "warm", a colour that no longer exists, so
 *  an unrecognised id (a hand-edited save's `litBy` key, a colour renamed in a later build) returned
 *  undefined and every caller threw on `.label` / `.tube` / `.role`. Keep this pointing at a key that
 *  is actually in LIGHT_COLORS. */
export function lightColor(id: string | undefined): LightColor {
  return (id && LIGHT_COLORS[id]) || LIGHT_COLORS.white!;
}
/** Clamp a chosen colour to what the tier unlocks (so a downgrade/old save can't keep
 *  a locked colour). Returns a valid colour id for the tier. */
export function colorForTier(tier: number, chosen: string | undefined): string {
  const allowed = colorsForTier(tier);
  return chosen && allowed.includes(chosen) ? chosen : allowed[0]!;
}

/** Number of shelves that exist but DON'T yet have a light — the effective cap. */
export function openLightSlots(rackCount: number, tiers: number, placed: ReadonlySet<string>): number {
  let open = 0;
  for (let r = 0; r < rackCount; r++) {
    for (let t = 0; t < tiers; t++) {
      if (!placed.has(lightKey(r, t))) open++;
    }
  }
  return open;
}
